export const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export const formatNumber = (value: number, decimals: number): string => {
  const divisible = parseInt('1'.padEnd(decimals + 1, '0'), 10);

  return (Math.round(value * divisible) / divisible).toFixed(decimals);
};

export const formatNumberWithSeparators = (value: number, decimalPlaces: number): string => {
  const formatter = new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  });

  return formatter.format(value); /* e.g. 2,500.12 */
};

/**
 * Formats a number as GBP currency with thousands separator and 2 decimal places.
 * Use for grid formatForDisplay and any other £ display (en-GB: e.g. £1,234.56).
 * @param value Number to format, or null/undefined for zero
 * @returns e.g. '£1,234.56' or '£0.00'
 */
export const formatCurrencyGBP = (value: number | null | undefined): string => {
  if (value == null) {
    return '£0.00';
  }
  return `£${formatNumberWithSeparators(value, 2)}`;
};

/**
 * Formats a number for display with thousands separator (en-GB).
 * Use for grid formatForDisplay on numeric columns so all number formatters follow the same pattern.
 * @param value Number to format, or null/undefined
 * @param decimalPlaces Default 0 for integers
 * @returns Formatted string, or '0' / '0.00' when value is null/undefined
 */
export const formatNumberForDisplay = (
  value: number | null | undefined,
  decimalPlaces: number = 0
): string => {
  if (value == null) {
    return decimalPlaces === 0 ? '0' : formatNumberWithSeparators(0, decimalPlaces);
  }
  return formatNumberWithSeparators(value, decimalPlaces);
};

/**
 * Rounds the value to the nearest value that gives the specified number of decimal places.
 */
export const roundAccurately = (value: number, decimalPlaces: number) =>
  Number(Math.round(Number(value + 'e' + decimalPlaces)) + 'e' + decimalPlaces * -1);

export function formatLargeNumber(value: number): string {
  if (value >= 1_000_000) {
    return (value / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (value >= 1_000) {
    return (value / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return value.toString();
}

